// ALTIORA · Sprint 10 · MarketProfileOnboarding
// Modal permettant à chaque professionnel de configurer son profil marché :
// - sélection multi-segments avec pondération 0..100 (total = 100)
// - territoires (multi-select ISO)
// - types d'opportunités (optionnel)
// - exclusions (source_family, signal_type, ou couple SRC:TYPE)
// - validation live + sauvegarde + modification ultérieure
//
// AUCUN proxy sensible (nom / langue / apparence) — segmentation
// STRICTEMENT contextuelle. Le poids ne filtre pas la découverte,
// il ne fait que re-classer les opportunités déjà découvertes.
import { useEffect, useMemo, useState } from "react";
import { X, Save, RefreshCw, AlertTriangle, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { NAVY, GOLD, SLATE, CHAMPAGNE } from
  "@/components/altiora/PremiumUI";

const TERRITORY_OPTIONS = [
  { code: "QC", label: "Québec" },
  { code: "ON", label: "Ontario" },
  { code: "NB", label: "Nouveau-Brunswick" },
  { code: "NS", label: "Nouvelle-Écosse" },
  { code: "MB", label: "Manitoba" },
  { code: "AB", label: "Alberta" },
  { code: "BC", label: "Colombie-Britannique" },
  { code: "SK", label: "Saskatchewan" },
  { code: "PE", label: "Île-du-Prince-Édouard" },
  { code: "NL", label: "Terre-Neuve-et-Labrador" },
  { code: "YT", label: "Yukon" },
  { code: "NT", label: "Territoires du Nord-Ouest" },
  { code: "NU", label: "Nunavut" },
];

const OPPORTUNITY_TYPE_OPTIONS = [
  "PUBLIC_FINANCING", "INVESTMENT", "NEW_BUSINESS", "EXPANSION",
  "HIRING", "CONTRACT", "LEADERSHIP_CHANGE", "PUBLIC_EVENT",
  "PUBLIC_PROGRAM", "MEMBERSHIP", "COMMUNITY_SERVICE",
];

const EXCLUSION_PRESETS = [
  { value: "PROCUREMENT_SEAO", label: "Toutes les procurances SEAO" },
  { value: "NEWS_CNW", label: "Communiqués CNW" },
  { value: "REGISTRY_ISED", label: "Registre ISED" },
  { value: "PUBLIC_FIN_IQ", label: "Divulgations IQ" },
  { value: "NEWS_CNW:HIRING", label: "CNW · recrutement uniquement" },
];

export default function MarketProfileOnboarding({ open, onClose, onSaved }) {
  const [segments, setSegments] = useState([]);
  const [profile, setProfile] = useState(null);
  const [weights, setWeights] = useState({});
  const [territories, setTerritories] = useState(["QC"]);
  const [oppTypes, setOppTypes] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [customExclusion, setCustomExclusion] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const total = useMemo(
    () => Object.values(weights).reduce((a, b) => a + Number(b || 0), 0),
    [weights]);
  const isValid = total === 100 && territories.length > 0;

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [segs, current] = await Promise.all([
          api.get("/leads-engine/market-profile/segments"),
          api.get("/leads-engine/market-profile"),
        ]);
        setSegments(segs.data.segments || []);
        const p = current.data.profile || {};
        setProfile(current.data);
        setWeights(p.weights || {});
        setTerritories(p.territories?.length ? p.territories : ["QC"]);
        setOppTypes(p.opportunity_types || []);
        setExclusions(p.exclusions || []);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  function updateWeight(segId, value) {
    const v = Math.max(0, Math.min(100, Number(value) || 0));
    setWeights((w) => {
      const next = { ...w };
      if (v === 0) delete next[segId];
      else next[segId] = v;
      return next;
    });
  }

  function toggleTerritory(code) {
    setTerritories((t) => (t.includes(code)
      ? t.filter((x) => x !== code) : [...t, code]));
  }

  function toggleOppType(v) {
    setOppTypes((s) => (s.includes(v)
      ? s.filter((x) => x !== v) : [...s, v]));
  }

  function toggleExclusion(v) {
    setExclusions((s) => (s.includes(v)
      ? s.filter((x) => x !== v) : [...s, v]));
  }

  function addCustomExclusion() {
    const v = customExclusion.trim();
    if (!v || exclusions.includes(v)) return;
    setExclusions((s) => [...s, v]);
    setCustomExclusion("");
  }

  async function save() {
    if (!isValid) return;
    setSaving(true);
    setError(null);
    try {
      await api.post("/leads-engine/market-profile", {
        weights,
        territories,
        opportunity_types: oppTypes,
        exclusions,
      });
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  }

  function applyPreset(preset) {
    if (preset === "immigrant_80_20") {
      setWeights({ NEW_ARRIVALS: 80, ENTREPRENEURS: 20 });
    } else if (preset === "pme_focused") {
      setWeights({ PME_LEADERS: 60, ENTREPRENEURS: 30, PROFESSIONALS: 10 });
    } else if (preset === "professionals") {
      setWeights({ PROFESSIONALS: 60, SELF_EMPLOYED: 30, PME_LEADERS: 10 });
    } else if (preset === "balanced") {
      setWeights({
        NEW_ARRIVALS: 20, ENTREPRENEURS: 20, PME_LEADERS: 20,
        PROFESSIONALS: 20, GENERAL_MARKET: 20,
      });
    } else if (preset === "general") {
      setWeights({ GENERAL_MARKET: 100 });
    }
  }

  if (!open) return null;

  return (
    <div
      data-testid="market-profile-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.7)" }}
    >
      <div
        className="w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-xl shadow-2xl"
        style={{ background: "#FBF6EA", border: `1px solid ${CHAMPAGNE}` }}
      >
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between sticky top-0 z-10"
             style={{ background: NAVY, color: "#FBF6EA" }}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.24em]"
                 style={{ color: GOLD }}>
              ALTIORA · Configurer mon marché
            </div>
            <h2 className="font-serif text-xl mt-0.5">Profil marché personnel</h2>
          </div>
          <button data-testid="market-profile-close" onClick={onClose}
                  className="p-1 rounded hover:bg-white/10">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center" style={{ color: SLATE }}>
            <RefreshCw className="h-5 w-5 mx-auto animate-spin mb-2" />
            Chargement…
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Notice */}
            <div className="text-[11px] p-3 rounded flex gap-2 items-start"
                 style={{ background: `${GOLD}22`, color: NAVY,
                            border: `1px solid ${GOLD}55` }}>
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0"
                             style={{ color: GOLD }} />
              <div>
                Les poids <strong>re-classent</strong> les opportunités selon
                votre stratégie. Le radar continue de collecter <strong>toutes</strong> les
                sources actives : rien n'est jamais supprimé. Segmentation
                strictement contextuelle (organismes, événements, programmes) —
                aucun proxy sensible (nom, langue, origine présumée).
              </div>
            </div>

            {/* Presets */}
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.16em] mb-2"
                   style={{ color: SLATE }}>Presets rapides</div>
              <div className="flex gap-2 flex-wrap">
                {[
                  ["immigrant_80_20", "Nouveaux arrivants 80/20"],
                  ["pme_focused", "PME 60/30/10"],
                  ["professionals", "Professionnels"],
                  ["balanced", "Équilibré 5×20"],
                  ["general", "Marché général"],
                ].map(([id, label]) => (
                  <button key={id} onClick={() => applyPreset(id)}
                          data-testid={`preset-${id}`}
                          className="text-[11px] px-2.5 py-1 rounded border"
                          style={{ borderColor: `${NAVY}33`, color: NAVY,
                                     background: "white" }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Segments + weights */}
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-[10px] font-mono uppercase tracking-[0.16em]"
                     style={{ color: SLATE }}>
                  1 · Segments et pondération
                </div>
                <div className="text-[11px]"
                     style={{ color: total === 100 ? "#0F5E39" : "#B91C1C" }}
                     data-testid="market-profile-total">
                  Total : {total} / 100 %
                </div>
              </div>
              <div className="space-y-2">
                {segments.map((s) => {
                  const v = weights[s.id] || 0;
                  const active = v > 0;
                  return (
                    <div key={s.id}
                          data-testid={`segment-${s.id}`}
                          className={`rounded p-3 border ${active ? "bg-white" : "bg-transparent"}`}
                          style={{ borderColor: active ? GOLD : `${SLATE}22` }}>
                      <div className="flex gap-3 items-start">
                        <div className="flex-1">
                          <div className="font-serif text-[14px]"
                                style={{ color: NAVY }}>{s.label}</div>
                          <div className="text-[10px] mt-0.5"
                                style={{ color: SLATE }}>{s.description}</div>
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                          <input
                            type="range" min={0} max={100} step={5}
                            value={v}
                            onChange={(e) => updateWeight(s.id, e.target.value)}
                            data-testid={`weight-slider-${s.id}`}
                            className="w-32"
                          />
                          <input
                            type="number" min={0} max={100} value={v}
                            onChange={(e) => updateWeight(s.id, e.target.value)}
                            data-testid={`weight-input-${s.id}`}
                            className="w-14 text-[12px] px-1.5 py-0.5 border rounded text-right"
                          />
                          <span className="text-[10px]"
                                 style={{ color: SLATE }}>%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Territoires */}
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.16em] mb-2"
                   style={{ color: SLATE }}>2 · Territoires couverts</div>
              <div className="flex flex-wrap gap-1.5">
                {TERRITORY_OPTIONS.map((t) => {
                  const active = territories.includes(t.code);
                  return (
                    <button key={t.code}
                            data-testid={`territory-${t.code}`}
                            onClick={() => toggleTerritory(t.code)}
                            className={`text-[11px] px-2.5 py-1 rounded border ${active ? "font-medium" : ""}`}
                            style={{
                              background: active ? NAVY : "white",
                              color: active ? "#FBF6EA" : NAVY,
                              borderColor: active ? NAVY : `${SLATE}33`,
                            }}>
                      {t.code} · {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Opportunity types */}
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.16em] mb-2"
                   style={{ color: SLATE }}>
                3 · Types d'opportunités (optionnel)
              </div>
              <div className="flex flex-wrap gap-1.5">
                {OPPORTUNITY_TYPE_OPTIONS.map((v) => {
                  const active = oppTypes.includes(v);
                  return (
                    <button key={v}
                            data-testid={`opp-type-${v}`}
                            onClick={() => toggleOppType(v)}
                            className="text-[10px] px-2 py-1 rounded border font-mono"
                            style={{
                              background: active ? `${GOLD}44` : "white",
                              color: NAVY,
                              borderColor: active ? GOLD : `${SLATE}33`,
                            }}>
                      {v}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Exclusions */}
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.16em] mb-2"
                   style={{ color: SLATE }}>4 · Exclusions (dé-prioritisation)</div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {EXCLUSION_PRESETS.map((e) => {
                  const active = exclusions.includes(e.value);
                  return (
                    <button key={e.value}
                            data-testid={`exclusion-${e.value}`}
                            onClick={() => toggleExclusion(e.value)}
                            className="text-[10px] px-2 py-1 rounded border"
                            style={{
                              background: active ? "#FCA5A588" : "white",
                              color: NAVY,
                              borderColor: active ? "#DC2626" : `${SLATE}33`,
                            }}>
                      {active ? "✕ " : ""}{e.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <input value={customExclusion}
                        data-testid="custom-exclusion-input"
                        onChange={(e) => setCustomExclusion(e.target.value)}
                        placeholder="Exclusion custom (ex: PROCUREMENT_SEAO:junk)"
                        className="flex-1 text-[11px] px-2 py-1 border rounded" />
                <button onClick={addCustomExclusion}
                        data-testid="custom-exclusion-add"
                        className="text-[11px] px-3 py-1 rounded border"
                        style={{ borderColor: NAVY, color: NAVY,
                                    background: "white" }}>Ajouter</button>
              </div>
              {exclusions.filter((v) => !EXCLUSION_PRESETS.find(
                (p) => p.value === v)).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {exclusions
                    .filter((v) => !EXCLUSION_PRESETS.find(
                      (p) => p.value === v))
                    .map((v) => (
                      <span key={v}
                            className="text-[10px] px-2 py-0.5 rounded border font-mono"
                            style={{ background: "#FCA5A544", color: NAVY,
                                       borderColor: "#DC262666" }}>
                        {v}{" "}
                        <button onClick={() => toggleExclusion(v)}
                                data-testid={`exclusion-remove-${v}`}
                                className="ml-1 hover:font-bold">×</button>
                      </span>
                    ))}
                </div>
              )}
              <div className="text-[10px] mt-1 italic" style={{ color: SLATE }}>
                Une exclusion abaisse le classement mais ne masque jamais
                l'opportunité — vous gardez la visibilité complète du radar.
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="text-[11px] p-2 rounded flex items-start gap-2"
                    style={{ background: "#FEE2E2", color: "#991B1B" }}
                    data-testid="market-profile-error">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>{error}</div>
              </div>
            )}
          </div>
        )}

        {/* Footer actions */}
        <div className="px-6 py-3 border-t flex items-center justify-between sticky bottom-0"
              style={{ background: "#FBF6EA",
                         borderColor: `${CHAMPAGNE}88` }}>
          <div className="text-[10px] italic" style={{ color: SLATE }}>
            {profile?.is_default
              ? "Aucun profil enregistré — profil par défaut appliqué."
              : `Dernière modif : ${profile?.profile?.updated_at || "—"}`}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose}
                    data-testid="market-profile-cancel"
                    className="text-[11px] px-3 py-1.5 rounded border"
                    style={{ borderColor: `${SLATE}55`, color: NAVY,
                                background: "white" }}>Annuler</button>
            <button onClick={save}
                    disabled={!isValid || saving}
                    data-testid="market-profile-save"
                    className="text-[11px] px-3 py-1.5 rounded flex items-center gap-1"
                    style={{
                      background: isValid ? NAVY : `${SLATE}44`,
                      color: "#FBF6EA",
                      cursor: (isValid && !saving) ? "pointer" : "not-allowed",
                    }}>
              <Save className="h-3 w-3" />
              {saving ? "Enregistrement…"
                       : (profile?.is_default ? "Enregistrer" : "Mettre à jour")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
